/**
 * The social sign-in providers — one list for every portal.
 *
 * Each portal renders its buttons from this list, the settings page names a
 * connected account from it, and the email change reads which provider an
 * address belongs to from it. The Clerk instance behind each portal must have
 * the same providers switched on (Clerk Dashboard → SSO connections): a button
 * for a provider the instance does not offer can only fail.
 *
 * Order is the display order (product decision, 26 Sep 2026): LinkedIn first —
 * the network people relocating for work already use — then Google,
 * Microsoft, Apple.
 */

export const SOCIAL_PROVIDERS = ['linkedin', 'google', 'microsoft', 'apple'] as const;

export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

/** Clerk's OAuth strategy for each provider. */
export const SOCIAL_PROVIDER_STRATEGY = {
  google: 'oauth_google',
  apple: 'oauth_apple',
  microsoft: 'oauth_microsoft',
  linkedin: 'oauth_linkedin_oidc',
} as const satisfies Record<SocialProvider, `oauth_${string}`>;

export type SocialProviderStrategy = (typeof SOCIAL_PROVIDER_STRATEGY)[SocialProvider];

/** Brand names — never translated. */
export const SOCIAL_PROVIDER_NAME = {
  google: 'Google',
  apple: 'Apple',
  microsoft: 'Microsoft',
  linkedin: 'LinkedIn',
} as const satisfies Record<SocialProvider, string>;

/**
 * The provider behind a Clerk identifier, whatever its spelling: an external
 * account's `provider` (`google`, `linkedin_oidc`), a strategy
 * (`oauth_google`, `oauth_linkedin_oidc`) or an email's `linkedTo[].type`
 * (`oauth_apple`). `null` for anything that is not one of ours.
 */
export function socialProviderOf(identifier: string | null | undefined): SocialProvider | null {
  if (!identifier) return null;
  const bare = identifier.replace(/^oauth_(token_)?/, '').replace(/_oidc$/, '');
  return (SOCIAL_PROVIDERS as readonly string[]).includes(bare) ? (bare as SocialProvider) : null;
}
